import debounce from "lodash/debounce";
import { useObservable } from "micro-observables";
import { useCallback, useMemo, useState } from "react";
import { useServices } from "@core/servicesContext";
import { StorePresentation } from "@core/store/storePresentation";
import { PaginatedDataResult, usePaginatedStore, usePaginatedStoreWithArgs } from "@utils/hooks";
import { removeSpecialCaracters } from "@utils/strings";
import { Carousel } from "@domain/carousel/carousel";
import { CountryCode } from "@domain/currentProfile/countryCode";
import { Group } from "@domain/group/group";
import { ContentLicense } from "@domain/licence/contentLicense";
import { Article } from "@domain/lms/article/article";
import { Category } from "@domain/lms/category/category";
import { ContentLanguage } from "@domain/lms/contentLanguage";
import { ContentLevel } from "@domain/lms/contentLevel";
import { Course, CourseDuration, CourseProvider } from "@domain/lms/course/course";
import { Quiz } from "@domain/lms/quiz/quiz";
import { Organization } from "@domain/organization/organization";
import { OrganizationSubType, OrganizationType } from "@domain/organization/organizationType";
import { Scoring } from "@domain/ranking/ranking";
import { Person } from "@domain/user/user";
import { SearchDomain } from "./searchDomain";
import { SearchArguments } from "./searchService";

export function useSearchSuggestions(
	query: string,
	domain: SearchDomain
): PaginatedDataResult<Person | Course | Article | Quiz | Organization | Group | Carousel> {
	const { searchService } = useServices();
	const domainProvider = searchService.domainProviders[domain];
	const store = useMemo(() => {
		return new StorePresentation<Person | Course | Article | Quiz | Organization | Group | Carousel, SearchArguments>(
			domainProvider.store,
			domainProvider.search
		);
	}, [query, domain]);

	return usePaginatedStoreWithArgs<
		Person | Course | Article | Quiz | Organization | Group | Carousel,
		unknown,
		SearchArguments
	>(store, [domain], query);
}

export function useSuggestions(
	count: number,
	domain: SearchDomain
): PaginatedDataResult<Person | Course | Article | Quiz | Organization | Group | Carousel> {
	const { searchService } = useServices();
	const store = useMemo(
		() =>
			new StorePresentation<Person | Course | Article | Quiz | Organization | Group | Carousel>(
				searchService.domainProviders[domain].store,
				() => searchService.domainProviders[domain].suggest(count)
			),
		[count]
	);
	return usePaginatedStore<Person | Course | Article | Quiz | Organization | Carousel | Group>(store, [domain, count]);
}

export function useUserSearchWithExclusion(
	query: string,
	groupId?: string,
	organizationId?: string
): PaginatedDataResult<Person> {
	const { userService } = useServices();
	const store = useMemo(() => {
		return new StorePresentation<Person, [string, string?, string?]>(
			userService.profilesStore,
			userService.searchUsersWithExclusion
		);
	}, [query]);

	return usePaginatedStoreWithArgs<Person, unknown, [string, string?, string?]>(
		store,
		[],
		query,
		groupId,
		organizationId
	);
}

export function useSearchResult(
	query: string,
	domain: SearchDomain,
	categories?: Category[],
	levels?: ContentLevel[],
	languages?: ContentLanguage[],
	durations?: CourseDuration[],
	providers?: CourseProvider[],
	orgTypes?: OrganizationType[],
	orgSubTypes?: OrganizationSubType[],
	countries?: CountryCode[],
	license?: ContentLicense[],
	scoring?: Scoring[],
	rating?: number
): PaginatedDataResult<Person | Course | Article | Quiz | Organization | Group | Carousel> {
	const { searchService } = useServices();
	return usePaginatedStoreWithArgs<
		Person | Course | Article | Quiz | Organization | Group | Carousel,
		unknown,
		SearchArguments
	>(
		searchService.stores[domain],
		[domain],
		removeSpecialCaracters(query),
		categories,
		levels,
		languages,
		durations,
		providers,
		orgTypes,
		orgSubTypes,
		countries,
		license,
		scoring,
		rating
	);
}

export function useLastSearches(): string[] {
	const { searchService } = useServices();
	return useObservable(searchService.lastSearches);
}

export function useDebouncedSearch(): { value: string; query: string; onChange: (v: string) => void } {
	const [searchValue, setSearchValue] = useState("");
	const [query, setQuery] = useState("");
	const debouncedChange = useCallback(
		debounce((v) => setQuery(v), 500, { leading: true, trailing: true }),
		[]
	);
	const onChange = useCallback((v: string) => {
		setSearchValue(v);
		debouncedChange(v);
	}, []);

	return { value: searchValue, onChange, query };
}
